import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UTCDeliveryPeriod } from '@epicuro-next/ataraxia/models';
import { IFilterValue } from '@epicuro-next/data/reporting-api';
import { formatUTCRange, Placement } from '@epicuro-next/utilities';
import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';

import { DateFilterOptionLabel, dateFilterOptions } from '../../data';
import { BestPerformingListQuery, DateFilterOptionType } from '../../models';
import { deductDaysFilter } from '../../utils';

@Component({
  selector: 'epc-dashboard-header-controls',
  templateUrl: './dashboard-header-controls.component.html',
  styleUrls: ['./dashboard-header-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DashboardHeaderControlsComponent implements OnInit, OnChanges {
  @Input()
  public minDate: Date;

  @Input()
  public isBrandFilterAvailable = false;

  @Input()
  public brandOrgs: IFilterValue[];

  @Input()
  public selectedBrandOrgsIds: string[] = [];

  @Input()
  public preselectedDateRangeFilterValue: DateFilterOptionType =
    DateFilterOptionType.Last7Days;

  @Input()
  public preselectedBrandOrg?: string;

  @Output()
  public updateParams = new EventEmitter<Partial<BestPerformingListQuery>>();

  public selectedDateOption$$ = new BehaviorSubject<string>(
    dateFilterOptions[0].label,
  );

  public selectedOptionText$ = this.selectedDateOption$$.asObservable();

  public dateFilterOptions = dateFilterOptions;

  public placement = Placement;
  public maxDate = DateTime.utc().toJSDate();

  public defaultTextBrandOrg = $localize`:AnalyticsDashboard|Best Performing Lists Selected
                Title@@bestPerformingListsSelectedTitle:Global`;

  private readonly keysToCheckForPreselectingBrandOrg: (keyof DashboardHeaderControlsComponent)[] =
    ['brandOrgs', 'preselectedBrandOrg'];

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.keys(changes).filter((key) =>
        this.keysToCheckForPreselectingBrandOrg
          .map((k) => k.toString())
          .includes(key),
      ).length > 0
    ) {
      this.preselectBrandOrg();
    }
  }

  public ngOnInit() {
    this.preselectDefaultDateFilter();
    this.preselectBrandOrg();
  }

  public filterListByDate({ id, label }: DateFilterOptionLabel) {
    const dateRange = deductDaysFilter(id);

    this.updateParams.emit({ dateRange });
    this.selectedDateOption$$.next(label);
  }

  public setBrandFilterSelection(selectedId: string[]) {
    this.updateParams.emit({ brandOrgIds: selectedId });
  }

  public customRangeSelected(dateRange: Required<UTCDeliveryPeriod>) {
    this.updateParams.emit({ dateRange });

    const rangeText = formatUTCRange(`${dateRange.from}/${dateRange.to}`);

    this.selectedDateOption$$.next(rangeText);
  }

  public trackByValue(_: number, item: IFilterValue) {
    return item.key;
  }

  private preselectDefaultDateFilter(): void {
    const dateFilterToSet =
      dateFilterOptions.find(
        (dfo) => dfo.id === this.preselectedDateRangeFilterValue,
      ) ?? this.dateFilterOptions[0];
    this.filterListByDate(dateFilterToSet);
  }

  private preselectBrandOrg(): void {
    if (!this.preselectedBrandOrg || !this.brandOrgs) {
      return;
    }

    const brandOrgId = this.brandOrgs.find(
      (bo) => bo.key.toLowerCase() === this.preselectedBrandOrg?.toLowerCase(),
    );

    if (brandOrgId) {
      this.selectedBrandOrgsIds = [brandOrgId.key];
    }
  }
}
